import styled from '@emotion/styled'

import { breakpoint } from '../../__styling/style-functions'
import { calcRem } from '../../__styling/style-functions/calc-rem'
import { metrics } from '../../__styling/settings/metrics'
import { colors } from '../../__styling/settings/colors'
import { supports } from '../../__helpers/at-supports-helper'
import { themedValue } from '../../__styling/themed-value'
import { withClass } from '@news-mono/web-common'
import { ThemeMarginKeys, ThemeMargins } from '../../__styling/settings/metrics'

type Gutters = boolean | ThemeMarginKeys

export interface StyledFeatureCompositionProps {
    hasTopSpacing?: boolean
    hasBackgroundFill?: boolean
    /** If true or undefined, will default to the outer margin of the theme */
    horizontalGutters?: Gutters
    /** If true or undefined, will default to the outer margin of the theme */
    verticalGutters?: Gutters
    verticalSpacing?: ThemeMarginKeys
    horizontalGap?: ThemeMarginKeys
}

interface ThemedMetrics {
    margins: ThemeMargins
}

const sidebarWidth = 300

const getGutterSize = (
    themedMetrics: ThemedMetrics,
    gutter?: Gutters,
): string | number => {
    return gutter === false
        ? 0
        : calcRem(
              typeof gutter === 'string'
                  ? themedMetrics.margins[gutter]
                  : themedMetrics.margins.outerMargin,
          )
}

export const StyledFeatureComposition = styled('div')<
    StyledFeatureCompositionProps
>(
    ({
        theme,
        verticalSpacing,
        horizontalGutters,
        verticalGutters,
        hasBackgroundFill,
        hasTopSpacing,
    }) => ({
        margin: '0 auto',
        width: '100%',
        display: 'table', // Grid FallBack
        tableLayout: 'fixed', // Required for IE so content does not overflow
        marginBottom:
            verticalSpacing && calcRem(theme.margins[verticalSpacing]),
        paddingLeft: getGutterSize(theme, horizontalGutters),
        paddingRight: getGutterSize(theme, horizontalGutters),
        paddingTop: hasTopSpacing ? getGutterSize(theme, verticalGutters) : 0,
        paddingBottom: getGutterSize(theme, verticalGutters),
        maxWidth: calcRem(1220),
        backgroundColor: themedValue(theme, {
            thewest: 'transparent',
            fallback: hasBackgroundFill ? colors.white : 'transparent',
        }),
    }),
    ({ theme, horizontalGap }) =>
        supports('display: grid', {
            display: 'grid', // Override display: table
            gridTemplateColumns: '100%',

            '@media print': {
                display: 'block',
            },

            [breakpoint('md')]: {
                gridColumnGap: horizontalGap
                    ? calcRem(theme.margins[horizontalGap])
                    : 0,
                gridTemplateColumns: `minmax(40px, 1fr) minmax(300px, 600px) minmax(${calcRem(
                    sidebarWidth,
                )}, 1fr)}`,
            },
        }),
)

StyledFeatureComposition.displayName = 'StyledFeatureComposition'

export const StyledMain = styled('div')<{ hasBackgroundFill?: boolean }>(
    ({ theme, hasBackgroundFill }) => ({
        display: 'table-cell',
        width: '100%',
        tableLayout: 'fixed',
        position: 'relative',
        backgroundColor: themedValue(theme, {
            thewest: hasBackgroundFill ? colors.white : 'transparent',
            fallback: 'transparent',
        }),
    }),
)
StyledMain.displayName = 'StyledMain'

export const StyledSidebar = withClass('hide-print')(
    styled('aside')<StyledFeatureCompositionProps>(
        ({ theme, horizontalGap }) => ({
            display: 'none',
            transform: 'translateZ(0)',

            [breakpoint('md')]: {
                display: 'table-cell',
                verticalAlign: 'top',
                width: sidebarWidth,
                boxSizing: 'content-box',
                paddingLeft: horizontalGap
                    ? calcRem(theme.margins[horizontalGap])
                    : undefined,
            },
        }),
        // Reset width and margin if grid is supported
        supports('display: grid', {
            width: 'auto',
            paddingLeft: 0,
        }),
    ),
)
StyledSidebar.displayName = 'StyledSidebar'

interface StyledAsideProps {
    horizontalGap?: ThemeMarginKeys
}

export const StyledAside = styled('aside')<StyledAsideProps>(
    ({ theme, horizontalGap }) => ({
        marginBottom: calcRem(metrics.thewest.margins.md),
        width: calcRem(40),
        display: 'none',

        [breakpoint('md')]: {
            display: 'table-cell',
            paddingRight: horizontalGap
                ? calcRem(theme.margins[horizontalGap])
                : undefined,
        },
    }),
    supports('display: grid', {
        display: 'block',
        width: 'auto',

        [breakpoint('md')]: {
            display: 'block',
            paddingRight: 0,
            marginBottom: 0,
            justifySelf: 'flex-end',
        },
    }),
)
StyledAside.displayName = 'StyledAside'
